import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowUpRight, ChevronDown, ChevronRight, Layers } from 'lucide-react';

import { Card, CardContent } from '@/ui/base/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import { Button } from '@/ui/base/button';
import { cn } from '@/nucleo/utilitarios';

import type { MixModalidade } from './tipos';

const COR_MODALIDADE: Record<string, { ponto: string; barra: string }> = {
  CONVENCIONAL: { ponto: 'bg-primary', barra: 'bg-primary' },
  ACESSIVEL: { ponto: 'bg-orange', barra: 'bg-orange' },
  CRECHE: { ponto: 'bg-warning', barra: 'bg-warning' },
};

const COR_ATENDIMENTO: Record<string, string> = {
  EMEI: 'bg-info',
  EMEF: 'bg-primary',
  EMEE: 'bg-orange',
  CEI: 'bg-warning',
  CCA: 'bg-accent',
  CCI: 'bg-muted-foreground',
};

/** BK-15 — "Mix por modalidade": ranking visual por modalidade + tipo de atendimento. */
export function MixPorModalidade({ dados }: { dados: MixModalidade }) {
  const [aberto, setAberto] = useState(false);
  const mesLabel = format(new Date(`${dados.mesReferencia}T00:00:00`), 'MMM/yy', { locale: ptBR });

  const modalidades = dados.porModalidade.map((m) => ({
    ...m,
    pct: dados.totalOs > 0 ? (m.qtdOs / dados.totalOs) * 100 : 0,
    cor: COR_MODALIDADE[m.codigo] ?? { ponto: 'bg-muted-foreground', barra: 'bg-muted-foreground' },
  }));
  const maxAtendimento = dados.porAtendimento.reduce((acc, a) => Math.max(acc, a.qtdOs), 0);
  const topAtendimento = dados.porAtendimento[0];

  return (
    <Card className="overflow-hidden rounded-2xl">
      <Collapsible open={aberto} onOpenChange={setAberto}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full rounded-t-2xl text-left transition hover:bg-muted/30 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Alternar mix por modalidade"
          >
            <div className="flex items-start gap-4 p-5">
              <div className="shrink-0 rounded-xl bg-primary/10 p-2.5">
                <Layers className="h-5 w-5 text-primary" aria-hidden />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      Mix por modalidade · {mesLabel}
                    </p>
                    <div className="mt-0.5 flex items-baseline gap-2">
                      <span className="text-2xl font-bold leading-none tabular-nums">
                        {dados.totalOs}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        OS ativas em {modalidades.length} modalidade
                        {modalidades.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 text-muted-foreground">
                    {aberto ? (
                      <ChevronDown className="h-4 w-4" aria-hidden />
                    ) : (
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    )}
                  </div>
                </div>

                <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-muted">
                  {modalidades.map((m) => (
                    <div
                      key={m.codigo}
                      className={cn('h-full transition-all', m.cor.barra)}
                      style={{ width: `${m.pct}%` }}
                      title={`${m.rotulo}: ${m.qtdOs} OS (${m.pct.toFixed(0)}%)`}
                    />
                  ))}
                </div>

                <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
                  {modalidades.map((m) => (
                    <div key={m.codigo} className="flex items-center gap-1.5 text-xs">
                      <span className={cn('h-2 w-2 rounded-full', m.cor.ponto)} aria-hidden />
                      <span className="font-medium">{m.rotulo}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {m.qtdOs} · {m.pct.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-5 px-5 pb-5 pt-0">
            <div className="h-px bg-border" />

            <div>
              <div className="mb-2.5 flex items-baseline justify-between">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Por tipo de atendimento
                </p>
                {topAtendimento && (
                  <p className="text-[11px] text-muted-foreground">
                    Maior volume:{' '}
                    <span className="font-medium text-foreground">{topAtendimento.rotulo}</span>
                  </p>
                )}
              </div>
              <ul className="space-y-2">
                {dados.porAtendimento.map((a) => {
                  const pct = dados.totalOs > 0 ? (a.qtdOs / dados.totalOs) * 100 : 0;
                  const largura = maxAtendimento > 0 ? (a.qtdOs / maxAtendimento) * 100 : 0;
                  return (
                    <li
                      key={a.codigo}
                      className="grid grid-cols-[88px_1fr_auto] items-center gap-3 text-sm"
                    >
                      <span className="truncate font-medium">{a.rotulo}</span>
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            COR_ATENDIMENTO[a.codigo] ?? 'bg-primary',
                          )}
                          style={{ width: `${largura}%` }}
                        />
                      </div>
                      <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                        {a.qtdOs} OS · {pct.toFixed(0)}%
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="flex justify-end">
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/operacao/financeiro/fechamento-mensal">
                  Abrir fechamento mensal <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
